import { DestroyRef, inject, Injectable, Pipe, PipeTransform } from "@angular/core";
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from "@angular/router";
import { Store } from "@ngxs/store";
import { NgxRolesService } from "ngx-permissions";
import { Observable, of } from "rxjs";
import { exhaustMap, map, withLatestFrom } from "rxjs/operators";
import { CompanyRoleType } from "src/app/model/custom/ICompany";
import { StoredAuthState } from "src/app/shared/state/stored-auth.state";

export enum Roles {
	SystemAdministrator = "SystemAdministrator",
	Company = "Company",
	CompanyAdministrationUser = "CompanyAdministrationUser",
	CompanySiteAdministrationUser = "CompanySiteAdministrationUser",
	CompanySiteManager = "CompanySiteManager",
	CompanySupervisor = "CompanySupervisor",
}

export const MinimumRolePrefix = "MinimumRole";

export const MinimumRoles = {
	SystemAdministrator: `${MinimumRolePrefix}${Roles.SystemAdministrator}`,
	Company: `${MinimumRolePrefix}${Roles.Company}`,
	CompanyAdministrationUser: `${MinimumRolePrefix}${Roles.CompanyAdministrationUser}`,
	CompanySiteAdministrationUser: `${MinimumRolePrefix}${Roles.CompanySiteAdministrationUser}`,
	CompanySiteManager: `${MinimumRolePrefix}${Roles.CompanySiteManager}`,
	CompanySupervisor: `${MinimumRolePrefix}${Roles.CompanySupervisor}`,
};

@Injectable({
	providedIn: "root",
})
export class CompanyUserPermissionsService {
	private store: Store = inject(Store);
	private destroyRef: DestroyRef = inject(DestroyRef);
	private _companyRoles: Array<Roles> = [
		Roles.Company,
		Roles.CompanyAdministrationUser,
		Roles.CompanySiteAdministrationUser,
		Roles.CompanySiteManager,
		Roles.CompanySupervisor,
	];

	private _adminRoles: Array<Roles.SystemAdministrator> = [Roles.SystemAdministrator];

	private _allCompanyRoles: Array<Roles.SystemAdministrator | CompanyRoleType> = [
		...this._adminRoles,
		...this._companyRoles,
	];

	get companyRoles(): Array<Roles.SystemAdministrator | CompanyRoleType> {
		return this._allCompanyRoles;
	}

	canActivateForRole(
		minimumRole: Roles.SystemAdministrator | CompanyRoleType,
	): boolean {
		const allowedRoles = this._allCompanyRoles.slice(
			0,
			this._allCompanyRoles.indexOf(minimumRole) + 1,
		);
		const groups = this.store.selectSnapshot(StoredAuthState.groups);
		return allowedRoles.filter((i) => groups.includes(i)).length > 0;
	}
}

@Injectable({
	providedIn: "root",
})
export class CompanyRoleResolver implements Resolve<boolean> {
	private ngxRolesService: NgxRolesService = inject(NgxRolesService);
	private companyUserPermissions: CompanyUserPermissionsService = inject(
		CompanyUserPermissionsService,
	);

	isSystemAdministratorRole$: Observable<boolean> = inject(Store).select(
		StoredAuthState.isSystemAdministratorRole,
	);

	isCompanySupervisorRole$: Observable<boolean> = inject(Store).select(
		StoredAuthState.isCompanySupervisorRole,
	);

	isCompanySiteManagerRole$: Observable<boolean> = inject(Store).select(
		StoredAuthState.isCompanySiteManagerRole,
	);

	isCompanyAdministrationUserRole$: Observable<boolean> = inject(Store).select(
		StoredAuthState.isCompanyAdministrationUserRole,
	);

	isCompanyRootUserRole$: Observable<boolean> = inject(Store).select(
		StoredAuthState.isCompanyRootUserRole,
	);

	IsCompanySiteAdministratorRole$: Observable<boolean> = inject(Store).select(
		StoredAuthState.IsCompanySiteAdministratorRole,
	);

	resolve(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot,
	): Observable<any> | Promise<any> | any {
		return of(true).pipe(
			withLatestFrom(
				this.isSystemAdministratorRole$.pipe(
					map((permissions) => ({
						roleName: Roles.SystemAdministrator,
						permissions,
					})),
				),
				this.isCompanySupervisorRole$.pipe(
					map((permissions) => ({
						roleName: Roles.CompanySupervisor,
						permissions,
					})),
				),
				this.isCompanySiteManagerRole$.pipe(
					map((permissions) => ({
						roleName: Roles.CompanySiteManager,
						permissions,
					})),
				),
				this.IsCompanySiteAdministratorRole$.pipe(
					map((permissions) => ({
						roleName: Roles.CompanySiteAdministrationUser,
						permissions,
					})),
				),
				this.isCompanyAdministrationUserRole$.pipe(
					map((permissions) => ({
						roleName: Roles.CompanyAdministrationUser,
						permissions,
					})),
				),
				this.isCompanyRootUserRole$.pipe(
					map((permissions) => ({
						roleName: Roles.Company,
						permissions,
					})),
				),
			),
			exhaustMap(([val, ...roles]) => {
				/*
				 * Add single role permissions
				 * */
				roles.map((r) => {
					this.ngxRolesService.addRole(r.roleName, () => r.permissions);
				});
				/**
				 * Add minimum role permissions (order matters)
				 * can be applied for multiply roles
				 * prefix is `MinimumRole`
				 */
				this.companyUserPermissions.companyRoles.map((role) => {
					this.ngxRolesService.addRole(`${MinimumRolePrefix}${role}`, () =>
						this.companyUserPermissions.canActivateForRole(role),
					);
				});
				return of(val);
			}),
		);
	}
}

@Pipe({
	name: "permissions",
	standalone: false,
})
export class GlobalPermissionsPipe implements PipeTransform {
	transform(value: any): {
		roles: typeof Roles;
		minimumRoles: typeof MinimumRoles;
	} {
		return {
			roles: Roles,
			minimumRoles: MinimumRoles,
		};
	}
}
